/**
=========================================================
* Material Dashboard 2 PRO React TS - v1.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-2-pro-react-ts
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

// @mui material components
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";

// Material Dashboard 2 PRO React TS components
import MDBox from "@cloudpremise/portal_core/src/themes/mui/components/MDBox";
import MDTypography from "@cloudpremise/portal_core/src/themes/mui/components/MDTypography";
import MDButton from "@cloudpremise/portal_core/src/themes/mui/components/MDButton";

// Material Dashboard 2 PRO React TS examples components
import DashboardLayout from "@cloudpremise/portal_core/src/themes/mui/examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "@cloudpremise/portal_core/src/themes/mui/examples/Navbars/DashboardNavbar";
import Footer from "@cloudpremise/portal_core/src/themes/mui/examples/Footer";
import DataTable from  "@cloudpremise/portal_core/src/themes/mui/examples/Tables/DataTable";

import { useDataProvider, useAuthProvider, Loader } from '@cloudpremise/portal_core';
import { Shipment } from "../types/Shipment";
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import MDInput from "@cloudpremise/portal_core/src/themes/mui/components/MDInput";
import SearchForm from "../components/SearchForm";
import ItemModal from "../components/ItemModal";
import { useMaterialUIController } from "@cloudpremise/portal_core/src/themes/mui/context";

function Firestore({page, basename, history}): JSX.Element {
    const [ controller ] = useMaterialUIController();
    const { config } = controller;
    const [ authorized ] = useAuthProvider();
    const defaultState = {
        loading: false,
        loadingMore: false,
        response: null,
        viewMore: false,
        RF_LIMIT: 20,
        RF_OFFSET: null,
        searchForm: {
            UnitNo: '',
            WaybillDest: '',
            Origin: '',
            CurrentEvent: '',
        },
        itemModal: false,
        item: null,
    };

    const [state, setState] = React.useReducer((state: any, newState: Partial<any>) => ({
        ...state,
        ...newState
    }), defaultState);
    
    
    const shipment: Shipment = {
        sObjectTypeName: "Shipment",
        url: "/v1/sobject",
        params: {
            "RF_LIMIT": state.RF_LIMIT,
            "RF_OFFSET": state.RF_OFFSET,
            "search": state.searchForm,
        }
    }
    const [loading, data, adapterState, setAdapterState] = useDataProvider(shipment, {
        "provider": "firebase"
    }, false, (data) => {
        console.log(data);
        // if(state.response && state.response.nextKey){
        //     data = Object.assign(state.response, data);
        // }
        setState({
            response: data,
        });
    });

    const navigate = useNavigate();
    React.useEffect(() => {
        if(!authorized){
            return navigate("/auth/login-link", { replace: true });
        }
        const onScroll = () => {
            const scroll = window.scrollY;
            const height = document.body.scrollHeight - (window.innerHeight + 200);
            // console.log("Scroll = "+scroll+" Height = "+height);
            if(scroll >= height){
                handleLoadMore();
            }
        };

        // clean up code
        window.removeEventListener('scroll', onScroll);
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    });

    function onRowClick(event, row){
        event.preventDefault();
        console.log(row);
        setState({
            itemModal: true,
            item: row.original
        });
    }

    function getTableData(){
        const { response } = state;
        let rows = [];
        if(response && Array.isArray(response.result)){
            rows = response.result;
        }
        const tableData = {
            columns: [
                {
                    Header: "Unit",
                    accessor: "UnitNo",
                    Cell: (instance) => {
                        return (
                            <Link href="/" onClick={(event) => onRowClick(event, instance.row)}>
                                {instance.value}
                            </Link>
                        )
                    },
                },
                {
                    Header: "Shipper",
                    accessor: "Shipper",
                },
                {
                    Header: "Beneficial Owner",
                    accessor: "BeneficialOwner",
                },
                {
                    Header: "LE",
                    accessor: "LE",
                },
                // {
                //     Header: "Service Level",
                //     accessor: "ServiceLevel",
                // },
                // {
                //     Header: "Unit Type",
                //     accessor: "UnitType",
                // },
                // {
                //     Header: "Planned IG",
                //     accessor: "PlannedIG",
                // },
                // {
                //     Header: "Actual IG D/T",
                //     accessor: "ActualIGDateTime",
                // },
                // {
                //     Header: "Customer Request D/T",
                //     accessor: "CustomerRequestDateTime",
                // },
                {
                    Header: "Goal D/T",
                    accessor: "GoalDateTime",
                },
                {
                    Header: "Origin",
                    accessor: "Origin",
                },
                {
                    Header: "WaybillDest",
                    accessor: "WaybillDest",
                },
                {
                    Header: "Current Event",
                    accessor: "CurrentEvent",
                },
                {
                    Header: "Current Event D/T",
                    accessor: "CurrentEventDateTime",
                },
                // {
                //     Header: "BOL #",
                //     accessor: "BOLNo",
                // },
                // {
                //     Header: "Pickup #",
                //     accessor: "PickupNo",
                // },
                // {
                //     Header: "Lot Location",
                //     accessor: "LotLocation",
                // },
                // {
                //     Header: "Consignee",
                //     accessor: "Consignee",
                // },
                // {
                //     Header: "Notify Party",
                //     accessor: "NotifyParty",
                // }
            ],
            rows: rows
        };
        return tableData;
    }

    function handleLoadMore(){
        const { response } = state;
        if(!response){
            return;
        }
        if(!response.nextKey || adapterState.callApi || adapterState.loading){
            return;
        }
        setAdapterState({
            loading: false,
            callApi: true,
            viewMore: true,
            RF_OFFSET: response.nextKey
        });
    }

    function onSearch(data){
        if(adapterState.callApi || adapterState.loading){
            return;
        }
        setState({
            searchForm: data,
        });
        setTimeout(() => {
            setAdapterState({
                loading: false,
                callApi: true,
                viewMore: true,
                RF_OFFSET: null
            });
        }, 100);
    }
    const tableData = getTableData();

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox my={config.hideLayout ? 0 : 3}>
                <MDBox 
                    display="flex"
                    justifyContent="space-between"
                    alignItems="flex-start"
                    mb={2}
                    sx={({ breakpoints }) => ({
                        [breakpoints.down("md")]: {
                            flexDirection: "column"
                        }
                    })}
                >
                    <h3>Firestore</h3>
                    <SearchForm
                        {...state.searchForm}
                        onSearch={onSearch}
                    />
                </MDBox>
                <Card>
                    {
                        loading ?
                            <Loader p={2} color="info" />
                        :
                        <DataTable
                            table={getTableData()}
                            canSearch={false}
                            isSorted={false}
                            entriesPerPage={false}
                            rowsCount={tableData.rows.length}
                            manualPagination
                            loading={adapterState.viewMore}
                        />
                    }
                </Card>
                {
                    (state.itemModal) ?
                        <ItemModal
                            open={(state.itemModal)}
                            item={state.item}
                            onCancel={(item) => setState({itemModal: false})}
                        />
                    :
                    null
                }
            </MDBox>
            <Footer />
        </DashboardLayout>
    );
}

export default Firestore;
